import { useState } from "react";
import EntityService from "../../services/EntityService";
import { handleFormValidationErrors, showToast } from "../../utils/utils";

const useInstructorCrud = (endpoint, initialFormData) => {
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for the field when user types
    setFormErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  // Fetch All entites
  const getAllEntites = async (token, params) => {
    setLoading(true);
    try {
      const response = await EntityService.getAllEnties(
        token,
        endpoint.app_instructors_all,
        params
      );
      setError(null);
      return response.data;
    } catch (error) {
      handleFormValidationErrors(error, setFormErrors, setError);
      showToast(
        "Error",
        error.message || "Failed to fetch all entites",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const getEntity = async (token, entityId) => {
    setLoading(true);
    try {
      const response = await EntityService.getEntity(
        token,
        endpoint.app_instructors_get,
        entityId
      );
      setFormData(response.data.data);
      setError(null);
      return response.data.data;
    } catch (error) {
      handleFormValidationErrors(error, setFormErrors, setError);
      showToast("Error", error.message || "Failed to fetch entity", "error");
    } finally {
      setLoading(false);
    }
  };

  // Add a new entity
  const addEntity = async (token, data) => {
    setLoading(true);
    try {
      const response = await EntityService.addEntity(
        token,
        endpoint.app_instructors_add,
        data
      );
      showToast("Success", response.data.message, "success");
      setError(null);
      return { success: true, data: response.data };
    } catch (error) {
      handleFormValidationErrors(error, setFormErrors, setError);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  // Update an entity
  const updateEntity = async (token, entityId, data) => {
    setLoading(true);
    try {
      const response = await EntityService.updateEntity(
        token,
        endpoint.app_instructors_update,
        entityId,
        data
      );
      showToast("Success", response.data.message, "success");
      setError(null);
      return { success: true, data: response.data };
    } catch (error) {
      handleFormValidationErrors(error, setFormErrors, setError);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  // Delete an entity
  const deleteEntity = async (token, entityId) => {
    setLoading(true);
    try {
      const response = await EntityService.deleteEntity(
        token,
        endpoint.app_instructors_delete,
        entityId
      );
      showToast("Success", response.data.message, "success");
      setError(null);
      return response.data;
    } catch (error) {
      handleFormValidationErrors(error, setFormErrors, setError);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  // Reset all errors
  const resetErrors = () => {
    setFormErrors({});
    setError(null);
  };

  return {
    formData,
    setFormData,
    loading,
    error,
    handleChange,
    getAllEntites,
    getEntity,
    addEntity,
    updateEntity,
    deleteEntity,
    formErrors,
    resetErrors,
  };
};

export default useInstructorCrud;
